import styled from "styled-components";
import { Link } from "react-router-dom";
import { tokens } from "../../../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../../../library/breakpoints";
import Button from "../../../../library/Button";

const { colors, typography, globals, effects } = tokens;

export const ModalOverlay = styled.div({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(29, 29, 29, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
});

export const ModalContainer = styled.div({
    position: 'relative',
    backgroundColor: colors.surfaces['container-background'],
    borderRadius: globals['border-radius'],
    border: `1px solid ${colors.surfaces['border-divider']}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
    maxWidth: '684px',
    alignItems: 'flex-start',
    padding: globals['spacing-7'],
    display: 'flex',
    flexDirection: 'column',
    gap: globals['spacing-5'],

});

export const ModalHeader = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    alignSelf: 'stretch',
    gap: globals['spacing-3'],
});

export const ModalTitle = styled.div({
    fontSize: typography.fontSize['3xl-24px'],
    fontWeight: typography.fontWeights.bold,
    color: colors.text.primary,
    lineHeight: typography.lineHeights[110],
});

export const CloseButton = styled.button({
    position: 'absolute',
    right: '11.505px',
    top: '7.807px',
    background: 'none',
    border: 'none',
    color: colors.neutral.grey[900],
    fontSize: typography.fontSize['lg-14px'],
    fontWeight: typography.fontWeights.light,
});

export const ModalDescription = styled.p({
     alignSelf: 'stretch',
    color: colors.text.secondary,
    fontSize: typography.fontSize['lg-14px'],
    lineHeight: typography.lineHeights['110'],
});

export const FieldGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    alignSelf: 'stretch',
});

export const FieldLabel = styled.label({
    fontSize: typography.fontSize['lg-14px'],
    color: colors.text.primary,
});

export const TogglesRow = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    gap: globals['spacing-6'],

    '& > label': {
        flex: '1 0 0',
        width: 'auto',
    },
});

export const ToggleContainer = styled.div({
    display: 'flex',
    padding: globals['spacing-3'],
    alignItems: 'center',
    gap: globals['spacing-2'],
    flex: '1 0 0',
    height: '44px',
    borderRadius: globals['border-radius'],
    border: `1px solid ${colors.surfaces['border-divider']}`,
    background: colors.surfaces['page-background'],

    '& > label': {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        paddingTop: '4px',
    },
});

export const ErrorMessage = styled.div({
    alignSelf: 'stretch',
    color: colors.states.alert.default,
    fontSize: typography.fontSize['lg-14px'],
    lineHeight: typography.lineHeights['110'],
});

export const ModalFooter = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: globals['spacing-3'],
});

export const CancelButton = styled.button({
    display: 'flex',
    height: '35px',
    padding: `${globals['spacing-2']} ${globals['spacing-4']}`,
    justifyContent: 'center',
    alignItems: 'center',
    gap: globals['spacing-1-5'],
    borderRadius: globals['border-radius'],
    background: colors.states.alert.default,
    color: colors.neutral.white,
    border: 'none',
    cursor: 'pointer',
});

export const multiSelectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? colors.states.info.default : colors.surfaces['border-divider'],
        display: 'flex',
        minHeight: globals['spacing-10'],
        padding: `${globals['spacing-1']} ${globals['spacing-1-5']}`,
        alignItems: 'center',
        gap: globals['spacing-1-5'],
        alignSelf: 'stretch',
        borderRadius: globals['border-radius'],
        border: `1px solid ${colors.surfaces['border-divider']}`,
        background: colors.surfaces['container-background'],
        boxShadow: '0 0 0 1px rgba(163, 163, 163, 0.50)',
        ':hover': {
            borderColor: colors.states.info.default,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignContent: 'center',
        padding: 0,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        alignItems: 'center',
    }),
    multiValue: (base) => ({
        ...base,
        display: 'flex',
        minHeight: '25px',
        padding: '3px 5px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: globals['spacing-2-5'],
        borderRadius: globals['border-radius'],
        background: colors.neutral.grey['200'],
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: colors.text.primary,
        fontSize: typography.fontSize['lg-14px'],
        fontWeight: typography.fontWeights.semibold,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: colors.text.primary,
        fontSize: typography.fontSize['lg-14px'],
        fontWeight: typography.fontWeights.light,
        ':hover': {
            backgroundColor: 'transparent',
            color: colors.text.primary,
        },
    }),
};

const cellText = {
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: typography.lineHeights["100"],
    color: colors.text.primary,
};

export const BulkProgressWrapper = styled.div({
    marginBottom: globals["spacing-6"],
});

export const GradebookPaginationFooter = styled.nav({
    width: "100%",
    minHeight: "68px",
    padding: globals["spacing-md"],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: globals["spacing-md"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        alignItems: "flex-start",
        flexDirection: "column",
    },
});

export const GradebookPaginationAmountText = styled.span({
    color: colors.text.secondary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "20px",
});

export const GradebookPaginationRight = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: globals["spacing-8"],
    flexWrap: "wrap",

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
        justifyContent: "space-between",
        gap: globals["spacing-3"],
    },
});

export const GradebookPaginationRowsPerPage = styled.label({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-sm"],
    margin: 0,
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

export const GradebookPaginationSelectWrapper = styled.div({
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
});

export const GradebookPaginationSelect = styled.select({
    width: "80px",
    height: globals["spacing-9"],
    padding: `${globals["spacing-2"]} ${globals["spacing-8"]} ${globals["spacing-2"]} ${globals["spacing-3"]}`,
    background: colors.surfaces["container-background"],
    boxShadow: effects.effects.shadow.default,
    borderRadius: globals["border-radius"],
    border: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    boxSizing: "border-box",
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "20px",
    cursor: "pointer",
    appearance: "none",

    "&:focus-visible": {
        outline: `${globals["spacing-px"]} solid ${colors.brand.blue["500"]}`,
        outlineOffset: globals["spacing-px"],
    },

    "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.6,
        background: colors.neutral.grey["100"],
    },
});

export const GradebookPaginationSelectIcon = styled.i({
    position: "absolute",
    right: globals["spacing-3"],
    top: "50%",
    transform: "translateY(-50%)",
    color: colors.text.secondary,
    pointerEvents: "none",
});

export const GradebookPaginationPageText = styled.span({
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

export const GradebookPaginationControls = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-sm"],
});

export const GradebookPaginationPageButton = styled.button({
    width: globals["spacing-8"],
    height: globals["spacing-8"],
    padding: 0,
    background: colors.surfaces["container-background"],
    borderRadius: globals["border-radius"],
    border: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.text.primary,
    cursor: "pointer",

    "&:hover:not(:disabled)": {
        borderColor: colors.brand.blue["500"],
        color: colors.brand.blue["500"],
    },

    "&:focus-visible": {
        outline: `${globals["spacing-px"]} solid ${colors.brand.blue["500"]}`,
        outlineOffset: globals["spacing-px"],
    },

    "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.5,
    },
});

export const GradebookCardBlock = styled.div({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    background: colors.neutral.white,
    border: `1px solid ${colors.surfaces["border-divider"]}`,
    borderRadius: globals["border-radius"],
    paddingTop: globals["spacing-4"],
    overflow: "hidden",
});

export const SearchFilterBar = styled.div({
    padding: `${globals["spacing-0"]} ${globals["spacing-4"]}`,
    marginBottom: globals["spacing-4"],
});

export const StudentAvatar = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: globals["spacing-7"],
    height: globals["spacing-7"],
    overflow: "hidden",
    borderRadius: "50%",
    background: colors.neutral.grey["200"],
    color: colors.text.secondary,
    flexShrink: 0,
    fontSize: typography.fontSize["lg-14px"],
});

export const FilterToolbarRow = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: globals["spacing-2"],

    "& .assessment-table-filter": {
        marginTop: "-2px",
    },
});

export const SearchBox = styled.label({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-3"],
    width: "166px",
    height: "35px",
    padding: `0 ${globals["spacing-3"]}`,
    background: colors.neutral.white,
    border: `1px solid ${colors.surfaces["border-divider"]}`,
    borderRadius: "8px",
    flexShrink: 0,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
    },
});

export const SearchIcon = styled.i({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: typography.fontSize["lg-14px"],
    height: typography.fontSize["lg-14px"],
    fontSize: typography.fontSize["lg-14px"],
    color: colors.text.secondary,
    flexShrink: 0,
});

export const SearchInput = styled.input({
    ...cellText,
    flex: "1 0 0",
    minWidth: 0,
    height: "100%",
    padding: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: colors.text.primary,

    "&::placeholder": {
        color: colors.text.secondary,
    },
});

export const filterDropdownButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    height: "35px",
    padding: `0 ${globals["spacing-2-5"]}`,
    borderRadius: globals["border-radius"],
    border: `1px solid ${colors.surfaces["border-divider"]}`,
    background: colors.neutral.white,
    boxShadow: effects.effects.shadow.default,
    fontFamily: globals["font-family"],
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    color: colors.text.primary,
    whiteSpace: "nowrap",
};

export const filterDropdownButtonActiveStyle = {
    ...filterDropdownButtonStyle,
    border: `1px solid ${colors.neutral.black}`,
    background: colors.neutral.black,
    color: colors.neutral.white,
};

export const ResetButton = styled.button({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    height: "35px",
    marginTop: "-2px",
    marginBottom: "4px",
    padding: `0 ${globals["spacing-2-5"]}`,
    borderRadius: globals["border-radius"],
    border: `1px solid ${colors.neutral.black}`,
    background: colors.neutral.black,
    boxShadow: effects.effects.shadow.default,
    color: colors.neutral.white,
    fontFamily: globals["font-family"],
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    whiteSpace: "nowrap",
    cursor: "pointer",

    "&:hover:not(:disabled)": {
        background: colors.neutral.grey["900"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

export const ExportButtonGroup = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-3"],
});

export const ExportButton = styled.button({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "none",
    padding: 0,
    fontSize: typography.fontSize["lg-14px"],
    color: colors.neutral.grey["800"],
    cursor: "pointer",

    "&:hover:not(:disabled)": {
        color: colors.text.primary,
    },

    "&:disabled": {
        opacity: 0.4,
        cursor: "not-allowed",
    },
});

const toneColors = {
    success: colors.states.success,
    alert: colors.states.alert,
    warning: colors.states.warning,
    info: colors.states.info,
    plum: { default: colors.brand["plum-orchid"]["500"] },
    neutral: { default: colors.text.secondary, 100: colors.neutral.grey["100"] },
};

const MODULE_STATUS_BACKGROUND = {
    pass: "rgba(22, 163, 74, 0.05)",
    fail: "rgba(196, 61, 81, 0.05)",
    exempt: "rgba(71, 72, 87, 0.05)",
    "not-enrolled": "rgba(227, 146, 25, 0.00)",
};

export const StyledTable = styled.table({
    width: "100%",
    border: "none",
    borderCollapse: "separate",
    borderSpacing: 0,
    ...cellText,
});

export const TableBookResponsive = styled.div({
    position: "relative",
    width: "100%",
    margin: 0,
    padding: 0,
});

export const TableBookScroll = styled.div({
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
});

export const TableBookScrollbarTrack = styled.div(({ $left, $right }) => ({
    position: "absolute",
    bottom: 0,
    left: `${$left}px`,
    right: `${$right}px`,
    height: "5px",
    overflowX: "auto",
    overflowY: "hidden",
}));

export const TableBookScrollbarInner = styled.div({
    height: "1px",
});

export const StyledHeaderRow = styled.tr({
    background: colors.neutral.white,
});

export const StyledHeaderCell = styled.th({
    ...cellText,
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    color: colors.neutral.grey["800"],
    textTransform: "none",
    height: "34px",
    padding: `${globals["spacing-0"]} ${globals["spacing-2"]}`,
    border: "none",
    borderBottom: `1px solid ${colors.surfaces["border-divider"]}`,
    whiteSpace: "nowrap",
    textAlign: "left",
    verticalAlign: "middle",
    boxSizing: "border-box",
    overflow: "hidden",
});

export const SortableHeaderCell = styled(StyledHeaderCell)({
    cursor: "pointer",
    userSelect: "none",

    "&:hover": {
        color: colors.text.primary,
    },
});

export const HeaderLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2-5"],
});

export const SortIcon = styled.i(({ $active }) => ({
    fontSize: typography.fontSize["sm-10px"],
    color: $active ? colors.brand["aie-blue"]["500"] : colors.neutral.grey["500"],
}));

export const CheckboxHeaderCell = styled(StyledHeaderCell)({
    padding: `${globals["spacing-0"]} ${globals["spacing-3"]}`,
    textAlign: "center",
});

export const StyledRow = styled.tr({
    height: "56px"
});

export const StyledCell = styled.td({
    ...cellText,
    height: "56px",
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    border: "none",
    borderBottom: `1px solid ${colors.surfaces["border-divider"]}`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    boxSizing: "border-box",
    overflow: "hidden",
});

export const CheckboxCell = styled(StyledCell)({
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    textAlign: "center",
});

export const CheckboxCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
});

const stickyPosition = ({ $left, $right, $width, $edge }) => ({
    position: "sticky",
    zIndex: 3,
    background: colors.neutral.white,
    ...($left !== undefined ? { left: `${$left}px` } : {}),
    ...($right !== undefined ? { right: `${$right}px` } : {}),
    ...($width !== undefined ? { width: `${$width}px`, minWidth: `${$width}px`, maxWidth: `${$width}px` } : {}),
    ...($edge === "left" ? {
        borderRight: `1px solid ${colors.brand.blue["500"]}`,
        boxShadow: "4px 0 6px -2px rgba(0, 0, 0, 0.08)",
    } : {}),
    ...($edge === "right" ? {
        borderLeft: `1px solid ${colors.brand.blue["500"]}`,
        boxShadow: "-4px 0 6px -2px rgba(0, 0, 0, 0.08)",
    } : {}),
});

export const StickyHeaderCell = styled(StyledHeaderCell)(stickyPosition);
export const StickySortableHeaderCell = styled(SortableHeaderCell)(stickyPosition);
export const StickyCheckboxHeaderCell = styled(CheckboxHeaderCell)(stickyPosition);
export const StickyCell = styled(StyledCell)(stickyPosition);
export const StickyCheckboxCell = styled(CheckboxCell)(stickyPosition);

export const StudentCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    minWidth: 0,
    height: "39px",
    overflow: "hidden",
});

export const StudentName = styled(Link)({
    ...cellText,
    color: colors.text.primary,
    fontWeight: typography.fontWeights.bold,
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    borderBottom: "1px solid transparent",

    "&:hover": {
        color: colors.text.primary,
        borderBottom: `1px solid ${colors.text.primary}`,
    },
});

export const OriginLink = styled(Link)({
    ...cellText,
    color: colors.text.primary,
    fontWeight: typography.fontWeights.bold,
    lineHeight: typography.lineHeights["110"],
    textDecoration: "none",
    borderBottom: "1px solid transparent",

    "&:hover": {
        color: colors.text.primary,
        borderBottom: `1px solid ${colors.text.primary}`,
    },
});

export const LatestIntakeText = styled.span({
    ...cellText,
    color: colors.text.primary,
    fontWeight: typography.fontWeights.bold,
    lineHeight: typography.lineHeights["110"],
});

export const ModuleHeaderTitle = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: globals["spacing-1"],
    width: "100%",
});

export const ModuleHeaderLink = styled(Link)({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    color: colors.text.link,
    textDecoration: "none",
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    whiteSpace: "nowrap",
    textBoxTrim: "trim-both",
    textBoxEdge: "cap alphabetic",
});

export const ModuleHeaderIcon = styled.i({
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.link,
    flexShrink: 0,
});

export const ModuleHeaderName = styled.p({
    margin: 0,
    width: 0,
    minWidth: "100%",
    fontSize: typography.fontSize["xs-8px"],
    fontWeight: typography.fontWeights.regular,
    color: colors.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

export const ModuleHeaderCell = styled(StyledHeaderCell)({
    minWidth: "130px",
    borderRight: `1px solid ${colors.surfaces["border-divider"]}`,
});

export const ModuleCell = styled(StyledCell)(({ $status }) => ({
    height: "56px",
    padding: globals["spacing-1"],
    position: "relative",
    textAlign: "center",
    borderRight: `1px solid ${colors.surfaces["border-divider"]}`,
    background: MODULE_STATUS_BACKGROUND[$status] || "transparent",
}));

export const ModuleCellContent = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-1"],
    width: "100%",
    height: "100%",
    minWidth: globals["spacing-24"],
});

export const ModuleCellCenter = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
});

export const ModuleIntakeLink = styled(Link)({
    position: "absolute",
    top: 0,
    left: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-0-5"],
    fontSize: typography.fontSize["xs-8px"],
    color: colors.text.link,
    textDecoration: "none",
    fontWeight: typography.fontWeights.semibold,
    padding: globals["spacing-1"]
});

export const ModuleMarkRow = styled.div({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
});

export const ModuleMarkText = styled.span({
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.primary,
});

export const ConditionIcon = styled.i(({ $tone = "neutral" }) => ({
    fontSize: typography.fontSize["sm-10px"],
    color: toneColors[$tone]?.default || toneColors.neutral.default,
}));

export const ExemptToggleButton = styled.button(({ $tone = "neutral" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    border: "none",
    background: "none",
    padding: 0,
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.medium,
    color: toneColors[$tone]?.default || toneColors.neutral.default,
    cursor: "pointer",
}));

export const ConditionTag = styled.span(({ $tone = "neutral" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    minHeight: "18px",
    maxHeight: "18px",
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    borderRadius: globals["border-radius"],
    border: "none",
    background: "transparent",
    color: toneColors[$tone]?.default || toneColors.neutral.default,
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
    cursor: "default",
}));

export const ConditionTagIcon = styled.i({
    fontSize: typography.fontSize["xs-8px"],
});

export const ExemptCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-2"],
    width: "100%",
    height: "100%",
});

export const ExemptLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    color: colors.neutral.grey["600"],
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
});

export const ExemptLabelIcon = styled.i({
    fontSize: typography.fontSize["xs-8px"],
    color: colors.neutral.grey["600"],
});

export const ResultBadge = styled.span(({ $tone = "neutral" }) => {
    if ($tone === "success") {
        return {
            display: "inline-flex",
            alignItems: "center",
            gap: globals["spacing-1"],
            padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
            borderRadius: globals["border-radius"],
            fontSize: typography.fontSize["sm-10px"],
            fontWeight: typography.fontWeights.semibold,
            background: toneColors.success.default,
            color: colors.neutral.white,
            whiteSpace: "nowrap",
            height: "18px",
        };
    }

    const tone = toneColors[$tone] || toneColors.neutral;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: globals["spacing-1"],
        padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
        borderRadius: globals["border-radius"],
        fontSize: typography.fontSize["sm-10px"],
        fontWeight: typography.fontWeights.semibold,
        background: $tone === "alert" ? "rgba(196, 61, 81, 0.1)" : "rgba(227, 146, 25, 0.1)",
        color: tone.default,
        whiteSpace: "nowrap",
        height: "18px",
    };
});

export const ActionCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: globals["spacing-2"],
});

export const ShadowedCalculateButton = styled(Button)({
    boxShadow: `${effects.effects.shadow.default} !important`,
});

export const NoRecordsMessage = styled.div({
    ...cellText,
    width: "100%",
    padding: globals["spacing-10"],
    textAlign: "center",
    color: colors.text.secondary,
});
